import { apiClient } from './client';
import type { components } from './schema.gen';
import type { PreviewOutput } from './imports';

/**
 * Client des profils de correspondance de colonnes.
 *
 * Un profil décrit comment les colonnes d'un fichier deviennent des événements
 * canoniques. Il est enregistré, versionné et réutilisable : c'est ce qui
 * remplace un développement Go par une saisie au back-office pour chaque
 * nouveau format de lot partenaire.
 */

export type MappingView = components['schemas']['MappingView'];
export type ImportColumn = components['schemas']['ImportColumnView'];
export type MappingSuggestion = components['schemas']['MappingSuggestionView'];
export type MappingTarget = components['schemas']['MappingTargetView'];
export type AttributeTargets = components['schemas']['AttributeTargetsView'];

/** Transformations disponibles, alignées sur celles que le moteur applique. */
export const TRANSFORMS = [
  { value: 'text', label: 'Texte' },
  { value: 'number', label: 'Nombre' },
  { value: 'date', label: 'Date' },
  { value: 'phone', label: 'Téléphone (E.164)' },
  { value: 'bool', label: 'Booléen' },
  { value: 'constant', label: 'Valeur fixe' },
  { value: 'ignore', label: 'Ignorer' },
] as const;

export type FieldMapping = {
  target: string;
  column?: string;
  constant?: string;
  transform: string;
};

export type EventTarget = {
  attributeType: string;
  sourceSystem: string;
  fields: FieldMapping[];
  idempotencyKey: string[];
  skipWhenEmpty?: string[];
  /**
   * N'émettre l'événement qu'une fois par valeur de clé dans le fichier.
   *
   * Distingue un fait de SUJET d'un fait d'OPÉRATION : deux transactions
   * identiques le même jour sont deux faits réels, mais le nom ou la date
   * d'enrôlement d'un marchand, répétés sur chacune de ses lignes, sont une
   * seule et même information.
   */
  oncePerKey?: boolean;
};

export type MappingDefinition = {
  region?: string;
  targets: EventTarget[];
};

export type ColumnReport = {
  sheet: string;
  sheets: string[];
  rowsScanned: number;
  headerSignature: string;
  columns: ImportColumn[];
  suggestions: MappingSuggestion[];
};

/**
 * GET /v1/imports/{id}/columns — lit l'en-tête et un échantillon SANS
 * interpréter le fichier. C'est ce qui permet de bâtir une correspondance sur
 * un format inconnu, et de repérer une colonne piégeuse (peu remplie, valeurs
 * aberrantes) avant de la mapper.
 */
export async function inspectImportColumns(
  id: number,
  sheet?: string,
  rows = 100,
): Promise<ColumnReport> {
  const { data, error } = await apiClient.GET('/v1/imports/{id}/columns', {
    params: { path: { id }, query: { sheet: sheet ?? '', rows } },
  });
  if (error || !data) throw new Error("lecture des colonnes impossible");
  return data as ColumnReport;
}

/**
 * GET /v1/import-mappings/targets — les champs mappables. L'enveloppe est
 * fixe ; les champs de payload sont dérivés du dictionnaire, donc ajouter un
 * type d'attribut suffit à le rendre mappable.
 */
export async function listMappingTargets(attributeType?: string): Promise<{
  envelope: MappingTarget[];
  types: AttributeTargets[];
}> {
  const { data, error } = await apiClient.GET('/v1/import-mappings/targets', {
    params: { query: { attributeType: attributeType ?? '' } },
  });
  if (error || !data) throw new Error('catalogue des cibles indisponible');
  return data as { envelope: MappingTarget[]; types: AttributeTargets[] };
}

/** GET /v1/import-mappings — profils enregistrés, filtrables par signature. */
export async function listImportMappings(headerSignature?: string): Promise<MappingView[]> {
  const { data, error } = await apiClient.GET('/v1/import-mappings', {
    params: { query: { headerSignature: headerSignature ?? '' } },
  });
  if (error || !data) return [];
  return (data.items ?? []) as MappingView[];
}

/**
 * POST /v1/import-mappings — enregistre une NOUVELLE VERSION du profil portant
 * ce nom. Les versions précédentes restent : un import ancien doit rester
 * interprétable tel qu'il l'a été.
 */
export async function saveImportMapping(input: {
  name: string;
  source?: string;
  headerSignature?: string;
  definition: MappingDefinition;
}): Promise<MappingView> {
  const { data, error } = await apiClient.POST('/v1/import-mappings', {
    body: {
      name: input.name,
      source: input.source ?? 'PARTNER_BATCH',
      headerSignature: input.headerSignature ?? '',
      definition: input.definition as unknown as Record<string, never>,
    },
  });
  if (error || !data) {
    const detail = (error as { detail?: string } | undefined)?.detail;
    throw new Error(detail ?? "profil refusé");
  }
  return data as MappingView;
}

/**
 * POST /v1/imports/{id}/try-mapping — applique une correspondance SANS
 * l'enregistrer.
 *
 * C'est ce qui rend l'ajustement possible : sans essai, il faudrait
 * enregistrer un profil pour voir le résultat, et chaque tâtonnement créerait
 * une version — polluant l'historique même qui sert à retracer l'interprétation
 * d'une donnée. Rien n'est persisté ici : ni profil, ni rattachement, ni
 * autorisation de lancer.
 */
export async function tryImportMapping(input: {
  importId: number;
  sheet?: string;
  definition: MappingDefinition;
  limit?: number;
}): Promise<PreviewOutput> {
  const { data, error } = await apiClient.POST('/v1/imports/{id}/try-mapping', {
    params: { path: { id: input.importId }, query: { limit: input.limit ?? 20 } },
    body: {
      sheet: input.sheet ?? '',
      definition: input.definition as unknown as Record<string, never>,
    },
  });
  if (error || !data) {
    const detail = (error as { detail?: string } | undefined)?.detail;
    throw new Error(detail ?? "essai impossible");
  }
  return data as PreviewOutput;
}
